/**
 * Cascade-driven step-routing helpers for the donor widget.
 *
 * Spec contract:
 *   ref:tier-3-calculation/widget-cascade
 *   ref:tier-5-cross-screen/widget-checkout-flow
 *   ref:tier-5-cross-screen/widget-frequency-step
 *
 * Pure functions over the cascade + legacy config — exported so the
 * test suite can exercise the four cascade paths without booting a
 * full DonationCheckout instance + DOM container.
 *
 * History:
 *   CHG-0171 W4 — introduced effective-allow_recurring resolution in
 *     checkout.ts as a private method.
 *   CHG-0907 / ITER-A4 (CHG-0909) — extracted the resolution into this
 *     pure helper + retired the widget-side defensive AND on
 *     paymentMethods.some(supports_recurring). The cascade
 *     (ref:tier-3-calculation/widget-cascade) is now authoritative
 *     for Layer 4 (provider capability) per the program plan §A4.
 */

/** Minimal cascade shape consumed by the step-routing helpers.
 *  Mirrors CascadedDonationSettings.allow_recurring (defined in
 *  ../types.ts; redeclared here so this module has no runtime
 *  dependency on checkout.ts and stays test-isolated). */
export interface CascadeRoutingInput {
  allow_recurring: boolean;
  /** CHG-0932 / ITER-C2 — extended for pledge-step routing. The C2
   *  pledge fallback (program plan §5.4) shows the pledge step when
   *  the cascade allows pledge but disallows recurring. */
  allow_pledge?: boolean;
}

/** Backward-compat CheckoutConfig surface consulted when cascade is
 *  not loaded yet (e.g. pre-/options-fetch initial render). */
export interface CheckoutConfigRoutingInput {
  disableRecurring?: boolean;
  showFrequency?: boolean;
  disablePledge?: boolean;
  /** Positive entity policy used by hosted CAMPAIGN/SESSION pages whose
   * pledge permission arrives as a flattened root mask. */
  allowPledges?: boolean;
}

export type DonorIntent = 'now' | 'later' | null;

export interface EnvelopeRoutingInput {
  constraints?: { pledge_allowed?: boolean } | null;
}

/**
 * Whether the checkout should ask the donor to choose between giving now and
 * pledging for later.
 *
 * The question renders before an unlocked entity has been selected, so any
 * pledgeable option is a positive signal. Hosted CAMPAIGN/SESSION pages may
 * carry the same entity policy as a flattened allowPledges root mask. No signal
 * means default-off; an explicit envelope/config veto still wins.
 */
export function shouldShowIntentStep(
  optionCascades: ReadonlyArray<CascadeRoutingInput | null | undefined> | null | undefined,
  envelope: EnvelopeRoutingInput | null,
  config: CheckoutConfigRoutingInput,
): boolean {
  if (config.disablePledge === true) return false;
  if (envelope?.constraints?.pledge_allowed === false) return false;
  if (config.allowPledges === true) return true;
  return optionCascades?.some((cascade) => cascade?.allow_pledge === true) ?? false;
}

/**
 * Effective allow_recurring decision for the donor surface.
 *
 * Resolution order:
 *   1. Cascade — when an entity is selected the server-resolved
 *      cascade.allow_recurring is authoritative. The cascade has
 *      already AND-ed:
 *        L1 organization.default_allow_recurring
 *        L2 entity.allow_recurring (Campaign / Designation /
 *           live GivingSession)
 *        L3 widget.disable_recurring
 *        L4 org_provider_capabilities.recurring  (post-A2)
 *      per ref:tier-3-calculation/widget-cascade.
 *   2. Legacy config flags — for embeds whose /options request
 *      hasn't completed yet. disableRecurring wins over
 *      showFrequency per CHG-0171 W2.
 *
 * Pre-A4 the widget also AND-ed in
 * `paymentMethods.some(supports_recurring)` as defense-in-depth on
 * top of the cascade (the legacy W4 widget-side capability check).
 * A4 retires that AND — the cascade's Layer 4 fold (added in A2)
 * makes it redundant. All four cascade-driven paths (L1/L2/L3/L4)
 * now resolve server-side; the widget trusts the answer.
 */
export function effectiveAllowRecurring(
  cascade: CascadeRoutingInput | null,
  config: CheckoutConfigRoutingInput,
): boolean {
  if (cascade) return cascade.allow_recurring;
  if (config.disableRecurring === true) return false;
  if (config.showFrequency === false) return false;
  return true;
}

/**
 * Should the donor see the frequency step at this point in the flow?
 *
 * Alias for `effectiveAllowRecurring` — same answer, named for the
 * intent at the goTo()-call-site. Kept as a separate symbol so a
 * future iteration (e.g. C2 pledge-fallback routing) can layer
 * pledge-step visibility on top without changing the cascade
 * read.
 */
export function shouldShowFrequencyStep(
  cascade: CascadeRoutingInput | null,
  config: CheckoutConfigRoutingInput,
): boolean {
  return effectiveAllowRecurring(cascade, config);
}

/**
 * Should the donor see the pledge step instead of the frequency step?
 *
 * CHG-0932 / ITER-C2 — pledge fallback per program plan §5.4. When
 * the cascade reports:
 *   - effective_allow_pledge === true  (entity opted into pledge)
 *   - effective_allow_recurring === false  (no recurring-capable
 *     provider OR entity disables recurring)
 * the widget surfaces the pledge step instead of frequency. The
 * donor commits a recurring-shaped pledge; cycle 0 is charged
 * immediately as a one-time donation; cycle 2+ is donor-re-engaged
 * via email reminders.
 *
 * When the cascade is null (legacy CheckoutConfig fallback) the
 * pledge step never renders — pledge requires server-resolved
 * effective_allow_pledge, which comes from the /options call. The
 * The selected cascade remains authoritative. The legacy showFrequency flag
 * does not gate pledge visibility, while an explicit disablePledge veto always
 * wins for backward-compatible embedded integrations.
 *
 * Spec contract:
 *   ref:tier-5-cross-screen/widget-pledge-step
 *   ref:tier-3-calculation/widget-cascade (allow_pledge output)
 *   program plan §5.4 (cycle-0 / cycle-2+ invariant)
 */
export function shouldShowPledgeStep(
  cascade: CascadeRoutingInput | null,
  donorIntent: DonorIntent = null,
  config: CheckoutConfigRoutingInput = {},
): boolean {
  if (config.disablePledge === true) return false;
  // An explicit Donate Now choice must never route back into a pledge.
  if (donorIntent === 'now') return false;

  const allowPledge =
    cascade?.allow_pledge === true
    || (donorIntent === 'later' && config.allowPledges === true);
  if (!allowPledge) return false;

  // Donor-elected pay-later is independent of recurring capability.
  if (donorIntent === 'later') return true;

  // Null intent preserves the pre-CHG-3410 capability fallback exactly.
  return cascade?.allow_recurring !== true;
}

/** True only for the explicit pledge-now/pay-later branch. Capability fallback
 * is also a pledge flow, but it retains the established cycle-zero payment. */
export function isNoPaymentPledgeFlow(
  cascade: CascadeRoutingInput | null,
  donorIntent: DonorIntent,
  config: CheckoutConfigRoutingInput = {},
): boolean {
  return donorIntent === 'later'
    && shouldShowPledgeStep(cascade, donorIntent, config);
}
